import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Typography, CircularProgress, Box } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { doSwaggerCall } from '../../../../hooks/useSwaggerClient';
import FhItemRow from './FhItemRow';

const ActivityImportPage = ({ companyId }) => {
    const [fhItems, setFhItems] = useState([]);
    const [activiterActivities, setActiviterActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedItem, setExpandedItem] = useState(null);
    const [itemDetails, setItemDetails] = useState({});

    const loadData = useCallback(
        async (silent = false) => {
            if (!silent) {
                setLoading(true);
            }
            setError(null);
            try {
                const [fhResult, actResult] = await Promise.all([
                    doSwaggerCall('activities', 'testIntegration', {
                        body: {
                            service: 'FareharborDirect',
                            companyId
                        }
                    }),
                    doSwaggerCall('activities', 'getActivitiesForAdmins', {
                        clientId: companyId,
                        pageSize: 10000
                    })
                ]);

                if (!fhResult.success) {
                    setError(fhResult.error || 'Failed to load FareHarbor items');
                    setLoading(false);
                    return;
                }

                setFhItems(fhResult.items || []);
                setActiviterActivities(actResult.activities || []);
            } catch (err) {
                setError(err.message || 'Failed to load data');
            }
            setLoading(false);
        },
        [companyId]
    );

    // Silent reload — refreshes data without showing full-page spinner
    const silentReload = useCallback(() => loadData(true), [loadData]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Build match map: fhItemId -> activiterActivity
    const matchMap = useMemo(() => {
        const map = {};
        activiterActivities.forEach(act => {
            if (act.api !== 'FareharborDirect') {
                return;
            }
            let opts = act.api_options;
            if (typeof opts === 'string') {
                try {
                    opts = JSON.parse(opts);
                } catch (e) {
                    return;
                }
            }
            if (opts && opts.itemId) {
                map[opts.itemId] = act;
            }
        });
        return map;
    }, [activiterActivities]);

    const handleToggleExpand = useCallback(
        async fhItemId => {
            if (expandedItem === fhItemId) {
                setExpandedItem(null);
                return;
            }
            setExpandedItem(fhItemId);

            // Fetch details if not cached
            if (!itemDetails[fhItemId]) {
                try {
                    const details = await doSwaggerCall('activities', 'getIntegrationInfo', {
                        body: {
                            service: 'FareharborDirect',
                            companyId,
                            itemId: fhItemId,
                            includePricing: true
                        }
                    });
                    setItemDetails(prev => ({ ...prev, [fhItemId]: details }));
                } catch (err) {
                    setItemDetails(prev => ({
                        ...prev,
                        [fhItemId]: { success: false, error: err.message }
                    }));
                }
            }
        },
        [expandedItem, itemDetails, companyId]
    );

    const handleImportActivity = useCallback(
        async fhItem => {
            try {
                // Re-upload FH image to our S3 if available
                let imageUrl = null;
                if (fhItem.image_cdn_url) {
                    try {
                        const imgResult = await doSwaggerCall('activities', 'reuploadImage', {
                            body: { url: fhItem.image_cdn_url }
                        });
                        if (imgResult.success && imgResult.url) {
                            imageUrl = imgResult.url;
                        }
                    } catch (imgErr) {
                        console.warn('Image re-upload failed, skipping:', imgErr.message);
                    }
                }

                const created = await doSwaggerCall('activities', 'createActivity', {
                    activity: { name: fhItem.name }
                });

                await doSwaggerCall('activities', 'updateActivity', {
                    activityId: created.id,
                    activity: {
                        name: fhItem.name,
                        description: fhItem.description_draftjs || null,
                        image: imageUrl,
                        api: 'FareharborDirect',
                        api_options: JSON.stringify({ useParent: true, itemId: fhItem.id }),
                        company_id: companyId,
                        vendor_sku: String(fhItem.id),
                        extra_info: '{}',
                        tags: [],
                        price_items: []
                    }
                });

                // Reload data silently to reflect the new match
                await silentReload();
                // Clear cached details so they reload
                setItemDetails(prev => {
                    const next = { ...prev };
                    delete next[fhItem.id];
                    return next;
                });
            } catch (err) {
                alert(`Failed to import activity: ${err.message}`);
            }
        },
        [companyId, silentReload]
    );

    const handleSyncActivity = useCallback(
        async (fhItem, existingActivity) => {
            try {
                // Re-upload FH image to our S3 if available
                let imageUrl = existingActivity.image || null;
                if (fhItem.image_cdn_url) {
                    try {
                        const imgResult = await doSwaggerCall('activities', 'reuploadImage', {
                            body: { url: fhItem.image_cdn_url }
                        });
                        if (imgResult.success && imgResult.url) {
                            imageUrl = imgResult.url;
                        }
                    } catch (imgErr) {
                        console.warn('Image re-upload failed, keeping existing:', imgErr.message);
                    }
                }

                // extra_info must be a JSON string for the API
                let extraInfo = existingActivity.extra_info || '{}';
                if (typeof extraInfo === 'object') {
                    extraInfo = JSON.stringify(extraInfo);
                }

                await doSwaggerCall('activities', 'updateActivity', {
                    activityId: existingActivity.id,
                    activity: {
                        name: fhItem.name,
                        description:
                            fhItem.description_draftjs || existingActivity.description || null,
                        image: imageUrl,
                        api: 'FareharborDirect',
                        api_options: JSON.stringify({ useParent: true, itemId: fhItem.id }),
                        company_id: companyId,
                        vendor_sku: String(fhItem.id),
                        extra_info: extraInfo,
                        tags: existingActivity.tags || [],
                        price_items: existingActivity.price_items || []
                    }
                });

                await silentReload();
                // Clear cached details so they reload
                setItemDetails(prev => {
                    const next = { ...prev };
                    delete next[fhItem.id];
                    return next;
                });
            } catch (err) {
                alert(`Failed to sync activity: ${err.message}`);
            }
        },
        [companyId, silentReload]
    );

    const handleRefresh = useCallback(() => {
        setItemDetails({});
        setExpandedItem(null);
        loadData();
    }, [loadData]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <div>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6"> Activity Import</Typography>
                <Typography
                    variant="body2"
                    color="primary"
                    style={{ cursor: 'pointer' }}
                    onClick={handleRefresh}
                >
                    Refresh
                </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
                {fhItems.length} items found, {Object.keys(matchMap).length} matched to existing
                activities
            </Typography>
            {fhItems.length === 0 && (
                <Alert severity="info">
                    No items found for this vendor. Check that the vendor has credentials
                    configured.
                </Alert>
            )}
            {fhItems.map(item => (
                <FhItemRow
                    key={item.id}
                    fhItem={item}
                    matchedActivity={matchMap[item.id] || null}
                    isExpanded={expandedItem === item.id}
                    onToggleExpand={() => handleToggleExpand(item.id)}
                    details={itemDetails[item.id] || null}
                    companyId={companyId}
                    onImportActivity={() => handleImportActivity(item)}
                    onSyncActivity={() => handleSyncActivity(item, matchMap[item.id])}
                />
            ))}
        </div>
    );
};

export default ActivityImportPage;
